import React, { Dispatch, SetStateAction, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, glyphs } from "../../../Styles";
import { MainContentPaddingTypes, NewInputList, NewModal, NewSelect, NewSelectList } from "../..";
import { field, FilterContent, NewFieldStyles, ValueType, createDisplayTypesFromOptions, NewInputListHandles, NewSelectListHandles, DisplayType } from "../../../Definitions";
import { AutoReport } from "../../../Services/Agrisys_GqlDataCaller/WidgetCalls/GQLCalls/autoReportCall";
import { Widget } from "../../../Services/Agrisys_GqlDataCaller/WidgetCalls/GQLCalls/wigdetCalls";
import { useAutoReport } from "../../../Services/Agrisys_GqlDataCaller/WidgetCalls/useAutoReport";
import { Graph_QL, System } from "../../../Services";
import { useFeedcurveOptions, useGroupOptions, useMilestoneOptions, useSectionOptions } from "../../_Components";
import NewButton from "../../BaseComponents/newButton";
import { useAppSelector } from "../../../Store";
import { selectLocationOptionsBySystem, selectSectionOptionBySystem } from "../../../Store/optionsHandlers/optionsCombinedReducer";

type AutoReportModal_Props = {
  system?: System;
  widget: Widget;
  autoReports: AutoReport[];
  closeModal: () => void;
  changeData: Dispatch<SetStateAction<Widget>>;
  connection: Graph_QL.Connection; 
};

export default function AutoReportModal({system, connection, autoReports, closeModal, changeData, widget}:AutoReportModal_Props) {
  const {t} = useTranslation()

  const groupOptions = useGroupOptions();
  const { feedCurveOptions } = useFeedcurveOptions();
  const milestoneOptions = useMilestoneOptions();
  const {allSectionOptions, locationOptions: allLocationOptions} = useSectionOptions()
  const sectionOptionsBySystem = useAppSelector((state) => selectSectionOptionBySystem(state, system?.system_id ? [system.system_id] : []))
  const locationOptionsBySystem = useAppSelector((state)=> selectLocationOptionsBySystem(state, system?.system_id ? [system.system_id] : []))
  const sectionOptions = system?.system_id ? sectionOptionsBySystem : allSectionOptions;
  const locationOptions = system?.system_id ? locationOptionsBySystem : allLocationOptions;

  const [autoReport, setAutoReport] = useState<AutoReport>();
  const [selectFields, setSelectFields] = useState<field[]>([{ id: -1 }]);
  const [addType, setAddType] = useState<"CREATE" | "EXCISTING">("CREATE");
  const [message, setMessage] = useState<string | null>(null)
  
  const { createAutoReport } = useAutoReport(connection);

  console.log("modal",autoReports)

  // The value only exist in the context of autoReport (for now) as such this will only be handled as an exception.
  const castedAdditionalValueForAutoReport = {label: "excess_remaining", key: "excess_remaining", id: 10, displayType: "percentage"} as unknown as DisplayType;
  const selectParameters = createDisplayTypesFromOptions({groupOptions, feedcurveOptions: feedCurveOptions, milestoneOptions, sectionOptions, locationOptions}).filter(({key}) => (key !== "system_id"));
  selectParameters.push(castedAdditionalValueForAutoReport);
  
  const filterParamOptions = selectParameters.map((type) => ({
    label: t(`params.${type.label}`, {defaultValue: type.label}), 
    id: type.id
  }));
  // #endregion
    
  // Handle filter type change
  const handleFilterTypeChange = (index: number, id: number) => {
    const updatedSelectFields = [...selectFields];
    if (updatedSelectFields[index].id === -1) {
      updatedSelectFields.push({ id: -1 });
    }
    updatedSelectFields[index] = { id, value: undefined };

    const selectedParameter = selectParameters.find((param) => param.id === id);
    if (selectedParameter) {
      const updatedFilterParameter = [...(autoReport?.filter || [])]; //
      updatedFilterParameter[index] = {
        key: selectedParameter.key,
        value: []
      };
      setAutoReport({...autoReport, auto_report_id: autoReport?.auto_report_id || 0, filter: updatedFilterParameter, name: ""});
    }
    setSelectFields(updatedSelectFields);
  };

  // Handle filter value change
  const handleFilterValueChange = (index: number, values: ValueType[]) => {
    const updatedFilterParameter = Array.isArray(autoReport?.filter) ? [...autoReport.filter] : [];
  
    if (!updatedFilterParameter[index]) return;
  
    updatedFilterParameter[index].value = values;
    if (updatedFilterParameter[index].key === "excess_remaining" && values[0] && values[0].label) {
      const nameLabel = t(`params.excess_remaining`, {defaultValue: "Remaining Yesterday"}) 
      updatedFilterParameter[index].value = [{...values[0], label: `${nameLabel} > ${values[0].label} %`}]
    }
    setAutoReport({...autoReport, auto_report_id: autoReport?.auto_report_id || 0, filter: updatedFilterParameter, name: ""});
  };

  // Remove a SelectField by index
  const handleRemoveField = useCallback((index: number) => {
    const updatedSelectFields = [...selectFields];
    updatedSelectFields.splice(index, 1);
    setSelectFields(updatedSelectFields);
  
    const updatedFilterParameter = Array.isArray(autoReport?.filter) ? [...autoReport.filter] : [];
    updatedFilterParameter?.splice(index, 1);
    setAutoReport({...autoReport, auto_report_id: autoReport?.auto_report_id || 0, filter: updatedFilterParameter, name: ""});

  }, [setAutoReport, autoReport, selectFields]);

  // #region | Rendered Fields
  const renderedFields = selectFields.map((field, index) => {
    // Remove already used options from optionslist
    const selectedIds = selectFields
    .filter((f, i) => i !== index && f.id !== undefined)
    .map((f) => f.id); 
    const availableOptions = filterParamOptions.filter((option) => !selectedIds.includes(option.id));
  
    return (
      <div key={field.id !== undefined ? `${field.id}-${index}` : index} style={{ 
        width: "100%", 
        display: "flex", 
        flexDirection: "column", 
        gap: "10px", 
        marginBottom: index === selectFields.length - 1 ? "0px" : "30px"
      }}>
        <div style={{ display: "flex", flexDirection: "row", gap: "0px", alignItems: "end", justifyContent: "space-between" }}>
          <NewSelect
            placeholder={t("select", {defaultValue: "select"})}
            key={index+String(field.id)}
            options={availableOptions}
            onChange={(option) => {
              option?.id && handleFilterTypeChange(index,  Number(option.id));
            }}
            // isRequired={addType === "CREATE" ? true : false}
            fieldOverrides={{
              fieldWidth: "260px",
              fieldStyle: NewFieldStyles.blueDarkSecondary
            }}
            valueOverrides={{
              defaultValue: filterParamOptions.find((parameter) => {return parameter.id === field.id})
            }}
          />
          {field.id !== -1 && ( <>
            <div style={{cursor: "pointer"}} onClick={() => handleRemoveField(index)}>
              <glyphs.Delete size="25px"/>
            </div>
          </> )}
        </div>
        {/* Content based on selected filter parameter */}
        {field.id !== -1 && (
          <div style={{    
            width: "100%",
            height: "fit-content",
            boxSizing: "border-box",
            backgroundColor: colors.greyLightSecondary,
            padding: "10px 5px",
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}>
            <FilterContent 
              filter={field} 
              index={index} 
              filterParameterTypes={selectParameters} 
              handleFilterValueChange={(values) => {handleFilterValueChange(index, values)}}
            />
          </div>
        )}
      </div>
    )
  })
  // #endregion

  const createInputRefList = useRef<NewInputListHandles>(null);
  const excistingSelectRefList = useRef<NewSelectListHandles>(null);

  const handleCreate = () => {
    if (addType === "CREATE") {
      let nameRes;
      let filterRes = autoReport?.filter;

      if (createInputRefList && createInputRefList.current) {
        nameRes = createInputRefList.current.refs.current[0].value;
      }
      
      if (!nameRes) {
        setMessage("missing name")
        setTimeout(() => setMessage(null), 2000)
        return;
      }
      if (!filterRes) {
        setMessage("missing filter")
        setTimeout(() => setMessage(null), 2000)
        return;
      } else {
        if (createAutoReport) {
          createAutoReport({
            data: {
              name: nameRes as string,
              filter: filterRes,
            }
          }).then((newId) => {if (newId) {changeData({...widget, auto_reports: [...widget.auto_reports, newId ]})}})
          setSelectFields([{ id: -1 }])
          closeModal()
        }
      }
    }
    if (addType === "EXCISTING") {
      if (excistingSelectRefList && excistingSelectRefList.current) {
        var autoReportRes = excistingSelectRefList.current.refs.current[0] && excistingSelectRefList.current.refs.current[0].value

        if (!autoReportRes?.id) {
          setMessage("missing auto report")
          setTimeout(() => setMessage(null), 2000)
          return;
        }
        
        changeData({...widget, auto_reports: [...widget.auto_reports, Number(autoReportRes.id)]})
        closeModal();
      }
    }
  }
  return (
    <NewModal
      header={t("widgets.autoReports.add", {defaultValue: "Add auto report"})}
      overrides={{
        mainContentPadding: MainContentPaddingTypes.secondary,
        secondaryContentScroll: true
      }}
      predefinedButtons={{ onClose: () => { closeModal(); setSelectFields([{ id: -1 }]) }}}
      secondaryContent={
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "start", justifyContent: "center", gap: "40px" }}>
        {/* CREATE NEW */}
        <div style={{ filter: addType === "CREATE" ? "opacity(1)" : "opacity(0.5)", transition: "filter 0.3s ease", position: "relative", width: "290px", minHeight: "86px"}} onClick={() => setAddType("CREATE")}>
          <div style={{position: "relative", zIndex: 1, display: "flex", flexDirection: "column", gap: "10px" }}>
            <NewInputList
              ref={createInputRefList}
              placeholders={[t("widgets.general.name", {defaultValue: "Name"})]}
              headers={[t("widgets.general.createNew", {defaultValue: "create new"})]}
              isRequired={addType === "CREATE" ? [true] : [false]}
              isClearable
              fieldOverrides={{
                fieldWidth: "290px"
              }}  
            />
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "start",
              flexDirection: "column",
              boxSizing: "border-box",
              width: "290px"
            }}>
              {renderedFields}
            </div>
          </div>
        </div>
        {/* ADD EXCISTING */}
        <div style={{ filter: addType === "EXCISTING" ? "opacity(1)" : "opacity(0.5)", transition: "filter 0.3s ease", position: "relative", width: "290px", height: "47px"}} onClick={() => setAddType("EXCISTING")}>
          <div style={{position: "relative", zIndex: 1}}>
            <NewSelectList
              ref={excistingSelectRefList}
              placeholders={[""]}
              headers={[t("widgets.autoReports.addExisting", {defaultValue: "add existing"})]}
              options={[autoReports
                .filter(a => !widget.auto_reports.includes(a.auto_report_id))
                .map((a) => ({
                  id: a.auto_report_id,
                  label: a.name
                }))
              ]}
              isRequired={addType === "EXCISTING" ? [true] : [false]}
              isClearable
              fieldOverrides={{
                fieldWidth: "290px"
              }}  
            />    
          </div>
        </div>

      </div>
      }
      footerContent={
        <NewButton 
          content={t("buttons.add", {defaultValue: "add"})}
          onClick={() => { handleCreate() }}
        />
      }
      footerMessage={message ?? ""}
    />
    
  )
}